import React, { useEffect, useMemo, useState } from 'react';
import { fetchDashboardData } from './api';
import { GroupDimension, PurchaseOrder, StatusFilter } from './types';
import { filterGroups, groupByPrimary } from './utils/groupHelpers';
import { DashboardHeader } from './DashboardHeader/DashboardHeader';
import { FilterBar } from './FilterBar/FilterBar';
import { VendorGroup } from './VendorGroup/VendorGroup';
import { ShareModal } from './ShareModal/ShareModal';
import './variables.scss';
import './Planning.scss';
import { ToastContainer } from 'react-toastify';
import SideNav from '../../components/shared/sidenav/SideNav';

export const Planning: React.FC = () => {
  const [data, setData] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [groupDimension, setGroupDimension] =
    useState<GroupDimension>('vendor');
  const [sourcingFilter, setSourcingFilter] = useState('');
  const [statusFilters, setStatusFilters] = useState<Set<StatusFilter>>(
    new Set()
  );
  const [inTransitOnly, setInTransitOnly] = useState(false);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [openSkuRows, setOpenSkuRows] = useState<Set<string>>(new Set());
  const [heldKeys, setHeldKeys] = useState<Set<string>>(new Set());
  const [shareKey, setShareKey] = useState<string | null>(null);

  // fetch once on mount
  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    fetchDashboardData()
      .then((records) => {
        if (cancelled) return;
        setData(records);
      })
      .catch((err) => {
        if (!cancelled)
          setError(
            err instanceof Error ? err.message : 'Failed to load dashboard data'
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const sourcingOptions = useMemo(
    () => Array.from(new Set(data.map((po) => po.sourcing))).sort(),
    [data]
  );

  // owner is a hard filter on the raw POs — everything else (overdue/no
  // plan/today/in-transit) is applied after grouping, since those depend on
  // the clubbed SKU totals and hold state.
  const ownerFilteredData = useMemo(() => {
    return data.filter(
      (po) => !sourcingFilter || po.sourcing === sourcingFilter
    );
  }, [data, sourcingFilter]);

  const allGroups = useMemo(
    () => groupByPrimary(ownerFilteredData, groupDimension, heldKeys),
    [ownerFilteredData, groupDimension, heldKeys]
  );

  const groups = useMemo(
    () => filterGroups(allGroups, { statusFilters, inTransitOnly }),
    [allGroups, statusFilters, inTransitOnly]
  );

  const handleGroupDimensionChange = (dimension: GroupDimension) => {
    setGroupDimension(dimension);
    setOpenGroups(new Set());
    setOpenSkuRows(new Set());
  };

  const toggleStatusFilter = (filter: StatusFilter) => {
    setStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(filter)) next.delete(filter);
      else next.add(filter);
      return next;
    });
  };

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSkuRow = (rowKey: string) => {
    setOpenSkuRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const toggleHold = (holdKey: string) => {
    setHeldKeys((prev) => {
      const next = new Set(prev);
      if (next.has(holdKey)) next.delete(holdKey);
      else next.add(holdKey);
      return next;
    });
  };

  // shared card always reflects the complete plan for that vendor/client —
  // looked up from allGroups (owner-filtered, hold-aware) rather than the
  // status/in-transit-filtered `groups`, so it's never a partial view
  const shareGroup = shareKey
    ? (allGroups.find((g) => g.key === shareKey) ?? null)
    : null;

  return (
    <div className="flex-row full-width full-height">
      <SideNav></SideNav>
      <div className="planning flex-column gap-12 full-width">
        <FilterBar
          groupDimension={groupDimension}
          onGroupDimensionChange={handleGroupDimensionChange}
          sourcingOptions={sourcingOptions}
          sourcingFilter={sourcingFilter}
          onSourcingFilterChange={setSourcingFilter}
          statusFilters={statusFilters}
          onToggleStatusFilter={toggleStatusFilter}
          inTransitOnly={inTransitOnly}
          onToggleInTransitOnly={() => setInTransitOnly((v) => !v)}
        />

        <div className="planning__groups flex-1 overflow-scroll">
          {loading && <div className="planning__empty">Loading…</div>}
          {!loading && error && <div className="planning__empty">{error}</div>}
          {!loading && !error && groups.length === 0 && (
            <div className="planning__empty">
              Nothing matches these filters.
            </div>
          )}
          {!loading &&
            !error &&
            groups.map((group) => (
              <VendorGroup
                key={group.key}
                group={group}
                groupDimension={groupDimension}
                isOpen={openGroups.has(group.key)}
                onToggle={() => toggleGroup(group.key)}
                openSkuRows={openSkuRows}
                onToggleSkuRow={toggleSkuRow}
                onToggleHold={toggleHold}
                onShare={setShareKey}
              />
            ))}
        </div>

        {shareGroup && (
          <ShareModal
            group={shareGroup}
            groupDimension={groupDimension}
            onClose={() => setShareKey(null)}
          />
        )}
      </div>
      <ToastContainer position="top-right" />
    </div>
  );
};
